'use client';

import {
	BASE_PRECISION_EXP,
	BigNum,
	MAX_LEVERAGE_ORDER_SIZE,
	MarketType,
	PerpMarketAccount,
	PositionDirection,
	ZERO,
} from '@velocity-exchange/sdk';
import { useMemo } from 'react';
import { useDebounce } from 'react-use';
import Env, {
	DEFAULT_MARKET_AUCTION_DURATION,
	syncGetCurrentSettings,
} from '../../environmentVariables/EnvironmentVariables';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import { validateMarketOrderParams } from '../../utils/invariants';
import { notify } from '../../utils/notifications';
import useDevSwitchIsOn from '../useDevSwitchIsOn';
import { deriveMarketOrderParams } from '@velocity-exchange/common';
import { dlog } from '../../dev';
import useGetOraclePriceForMarket from '../useGetOraclePriceForMarket';
import useMarkPrice from '../useMarkPrice';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import {
	getAdditionalEndPriceBuffer,
	isForceAuctionEndPriceUpToSlippage,
} from 'src/utils/slippage';
import { TRADE_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/tradePrep';

const useDevInvariants = () => {
	const devSwitchIsOn = useDevSwitchIsOn();
	const isUsingDevnet = Env.sdkEnv === 'devnet';

	const invariantHooksEnabled =
		devSwitchIsOn ||
		isUsingDevnet ||
		process.env.NEXT_PUBLIC_ISLOCAL === 'true';

	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const marketIndex = selectedMarket?.market?.marketIndex;
	const marketId = selectedMarket?.marketId;
	const direction = useDriftStore((s) =>
		s.tradeForm.side === 'buy'
			? PositionDirection.LONG
			: PositionDirection.SHORT
	);
	const baseSizeStringValue = useDriftStore(
		(s) => s.tradeForm.baseSizeStringValue
	);

	const orderType = useDriftStore((s) => s.tradeForm.orderType);

	const baseAmount = useMemo(
		() => BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP).val,
		[baseSizeStringValue]
	);
	const maxLeverageSelected = useDriftStore(
		(s) => s.tradeForm.maxLeverageSelected
	);
	const reduceOnly = useDriftStore((s) => s.tradeForm.reduceOnly);
	const { bestPrice, entryPrice, worstPrice } = useDriftStore(
		(s) =>
			s.tradeForm.priceImpact ?? {
				bestPrice: ZERO,
				entryPrice: ZERO,
				worstPrice: ZERO,
			}
	);
	const slippageTolerance = useDriftStore((s) => s.tradeForm.slippageTolerance);

	const getOraclePrice = useGetOraclePriceForMarket();
	const oraclePrice = marketId ? getOraclePrice(marketId) : undefined;
	const markPrice = useMarkPrice(marketId);

	const getMarketInfo = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const marketAccount = getMarketInfo(
		marketId?.marketIndex ?? 0,
		marketId?.marketType ?? MarketType.SPOT
	)?.account;

	// INVARIANT: MARKET ORDER PARAMS
	useDebounce(
		() => {
			if (!invariantHooksEnabled) return;
			if (!oraclePrice) return;
			if (!baseSizeStringValue) return;
			if (!selectedMarket) return;
			if (marketIndex === undefined) return;

			const settings = syncGetCurrentSettings();

			if (!settings) return;

			if (orderType === 'market' || orderType === 'oracle') {
				const forceAuctionEndPriceUpToSlippage =
					isForceAuctionEndPriceUpToSlippage(
						slippageTolerance,
						selectedMarket.marketId
					);

				// Check the market params invariants hold
				const marketOrderParams = deriveMarketOrderParams({
					marketType: selectedMarket?.isPerp
						? MarketType.PERP
						: MarketType.SPOT,
					marketIndex,
					direction,
					baseAmount: maxLeverageSelected
						? MAX_LEVERAGE_ORDER_SIZE
						: baseAmount,
					reduceOnly,
					allowInfSlippage: false,
					worstPrice,
					auctionDuration:
						settings?.auctionDuration ?? DEFAULT_MARKET_AUCTION_DURATION,
					auctionStartPriceOffset:
						TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
							settings?.auctionStartPriceOffset,
							selectedMarket.marketId
						),
					auctionEndPriceOffset: settings?.auctionEndPriceOffset,
					maxLeverageSelected,
					maxLeverageOrderSize: MAX_LEVERAGE_ORDER_SIZE,
					oraclePrice: oraclePrice.val,
					markPrice: markPrice?.val,
					bestPrice,
					entryPrice,
					auctionStartPriceOffsetFrom:
						TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffsetFrom(
							settings?.auctionStartPriceOffsetFrom,
							selectedMarket.marketId
						),
					auctionEndPriceOffsetFrom: settings?.auctionEndPriceOffsetFrom,
					// hardcode dynamic slippage here since it requires server call, seems unnecessary for useDevInvariants
					slippageTolerance:
						slippageTolerance === 'dynamic' ? 0.1 : slippageTolerance,
					isOracleOrder: settings?.oracleOffsetOrdersEnabled,
					additionalEndPriceBuffer: getAdditionalEndPriceBuffer(
						marketAccount as PerpMarketAccount
					),
					forceUpToSlippage: forceAuctionEndPriceUpToSlippage,
				});

				const result = validateMarketOrderParams(marketOrderParams);

				if (!result) {
					dlog(`market_params_invariant_breach`, `caught_order_params_breach`, {
						orderParams: marketOrderParams,
					});
					notify({
						type: 'warning',
						message: 'Caught Auction Params Invariant Breach',
						description: `If you submitted a market order for the current trade form state it would result in bad auction params, should debug this`,
					});
				}
			}
		},
		1000,
		[oraclePrice, baseSizeStringValue, selectedMarket, orderType]
	);
};

export default useDevInvariants;
